import { gsap } from 'gsap';
import { Head } from '@unhead/react';
import { CustomEase } from 'gsap/CustomEase';
import { SplitText } from 'gsap/SplitText';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import {
    IconChevronSprite,
    Polyline1,
    Polyline2,
    Polyline3,
    Polyline4,
    Polyline5
} from '$partials/icons/index.js';
import { Button } from '$partials/ui';
import { useLocale } from '$contexts/locale.jsx';
import BackgroundVideo from '$partials/media/background-video';
import { ModuleStats } from '$partials/modules/individual';
import { Stickers } from './stickers';

gsap.registerPlugin( SplitText, CustomEase);
CustomEase.create('smooth', '0.5, 0, 0, 1');

const polyLineIcons = [Polyline1, Polyline2, Polyline3, Polyline4, Polyline5];

/**
 * @param {any} richText
 * @returns {React.ReactNode}
 */
const renderHeroText = (richText) => {
    if (!richText?.json?.content) {
        return null;
    }

    return richText.json.content
        .filter((/** @type {any} */ block) => block.content?.length)
        .map((/** @type {any} */ block, /** @type {number} */ i) => (
            <div
                className='animated-statement__lines'
                key={i}>
                {block.content.map((/** @type {any} */ node, /** @type {number} */ j) => {
                    if (node.nodeType !== 'text') {
                        return null;
                    }
                    const isBold = node.marks?.some((/** @type {any} */ mark) => mark.type === 'bold');
                    return isBold ? (
                        <span
                            className='animated-statement__highlight animated-statement__highlight--orange'
                            key={j}>
                            {node.value}
                        </span>
                    ) : (
                        <React.Fragment key={j}>{node.value}</React.Fragment>
                    );
                })}
            </div>
        ));
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').HomePageHero}
 */
const HomePageHero = ({
    alignment = 'Center',
    bodyCopy = '',
    cta1Link,
    cta1Label,
    cta2Link,
    cta2Label,
    eyebrow = '',
    heroText,
    heroMedia,
    polyline,
    statistics,
    theme = ''
}) => {

    const PolylineIcon = polyline === 'None' ? null : polyLineIcons[Number(polyline) - 1] || null;

    const { formatURI } = useLocale();

    const isCenter = alignment === 'Center';
    // references
    const containerRef = useRef();
    const splitRef = useRef();
    const timelineRef = useRef();
    const mediaRef = useRef();
    const buttonsRef = useRef();
    const eyebrowRef = useRef();
    const bodyCopyRef = useRef();
    const statRowRef = useRef();
    const mediaLoadedRef = useRef(false);
    const polylineRef = useRef(null);

    const [shouldStartStickerIntro, setShouldStartStickerIntro] = useState(false);

    // Hide animated elements immediately on mount to prevent a flash before the debounced
    // createAnimation call fires and sets these states itself.
    useEffect(() => {
        if (eyebrowRef.current) { gsap.set(eyebrowRef.current, { autoAlpha: 0, yPercent: 10 }); }
        if (bodyCopyRef.current) { gsap.set(bodyCopyRef.current, { autoAlpha: 0, yPercent: 10 }); }
        if (buttonsRef.current) { gsap.set(buttonsRef.current, { autoAlpha: 0, yPercent: 10 }); }
        if (mediaRef.current) {
            // Use opacity (not autoAlpha) so visibility never changes — autoAlpha sets
            // visibility:hidden which GSAP's Observer detects and triggers ScrollTrigger.refresh(),
            // whose internal scrollTo(0,0) measurement pass then races against browser scroll restoration.
            gsap.set(mediaRef.current, { opacity: 0, yPercent: 35 });
        }
        if (statRowRef.current) { gsap.set(statRowRef.current, { autoAlpha: 0, yPercent: 20 }); }
        // opacity (not autoAlpha) so visibility is never touched — see mediaRef note above.
        // stickerRefs.current.filter(Boolean).forEach(el => gsap.set(el, { opacity: 0 }));
    }, []);

    const animateMediaIn = () => {
        if (!mediaRef.current) {return;}
        const tl = gsap.timeline({
            onComplete: () => setShouldStartStickerIntro(true)
        });
        tl.to(mediaRef.current, { opacity: 1, duration: 0.55, ease: 'power1.out' });
        tl.to(mediaRef.current, { yPercent: 0, duration: 1, ease: 'power4.out' }, 0);
    };

    // Builds the SplitText + staggered-line timeline. Called inside document.fonts.ready so
    // line-break measurement is accurate.
    const buildTextTimeline = (/** @type {Element} */ heading) => {
        splitRef?.current?.revert?.();

        // Make heading visible before SplitText measures it (Tailwind opacity-0 hides it initially)
        gsap.set(heading, { opacity: 1 });

        // Target the inner paragraph divs — the h1 contains nested block elements
        // so splitting the h1 directly produces no lines
        const lineTargets = containerRef.current.querySelectorAll('.animated-statement__lines');
        const split = SplitText.create(lineTargets, {
            type: 'lines',
            mask: 'lines',
            linesClass: 'line',
        });
        splitRef.current = split;

        // Lines positioned below their overflow-clip masks
        gsap.set(split.lines, { yPercent: 130 });

        const textTl = gsap.timeline({ defaults: { ease: 'smooth' } });
        timelineRef.current = textTl;

        if (eyebrowRef.current) {
            textTl.to(eyebrowRef.current, { autoAlpha: 1, yPercent: 0, duration: 0.7 }, 0);
        }

        textTl.to(split.lines, { yPercent: 0, duration: 0.7, stagger: 0.05 }, eyebrowRef.current ? 0.1 : 0);

        if (bodyCopyRef.current) {
            textTl.to(bodyCopyRef.current, { autoAlpha: 1, yPercent: 0, duration: 1.25, ease: 'linear' }, '-=0.3');
        }

        if (buttonsRef.current) {
            textTl.to(buttonsRef.current, { autoAlpha: 1, yPercent: 0, duration: 1.25, ease: 'linear' }, '-=1.1');
        }
    };

    // methods
    const createAnimation = () => {
        if (!containerRef.current) { return; }

        const heading = containerRef.current.querySelector('.animated-statement__text');
        if (!heading) { return; }

        // Set initial states immediately to prevent flash before fonts resolve
        if (eyebrowRef.current) { gsap.set(eyebrowRef.current, { autoAlpha: 0, yPercent: 10 }); }
        if (bodyCopyRef.current) { gsap.set(bodyCopyRef.current, { autoAlpha: 0, yPercent: 10 }); }
        if (buttonsRef.current) { gsap.set(buttonsRef.current, { autoAlpha: 0, yPercent: 10 }); }
        // Don't reset media if already loaded — resetting mid-animation causes a visible jump
        if (mediaRef.current && !mediaLoadedRef.current) { gsap.set(mediaRef.current, { opacity: 0, yPercent: 35 }); }
        if (statRowRef.current) { gsap.set(statRowRef.current, { autoAlpha: 0, yPercent: 20 }); }

        // Text + buttons: wait for fonts only so line-break measurement is accurate
        document.fonts.ready.then(() => buildTextTimeline(heading));

        if (statRowRef.current) {
            gsap.to(statRowRef.current, { autoAlpha: 1, yPercent: 0, duration: 0.6, ease: 'smooth', delay: 0.3 });
        }
    };

    useEffect(() => {
        createAnimation();

        const handleResize = () => splitRef?.current?.revert?.();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!mediaRef.current) { return; }

        const onLoaded = () => {
            mediaLoadedRef.current = true;
            animateMediaIn();
        };

        const img = mediaRef.current.querySelector('img');
        const video = mediaRef.current.querySelector('video');

        if (img) {
            if (img.complete) {
                onLoaded();
            } else {
                img.addEventListener('load', onLoaded, { once: true });
                img.addEventListener('error', onLoaded, { once: true });
                return () => {
                    img.removeEventListener('load', onLoaded);
                    img.removeEventListener('error', onLoaded);
                };
            }
        } else if (video) {
            if (video.readyState >= 2) {
                onLoaded();
            } else {
                video.addEventListener('loadeddata', onLoaded, { once: true });
                return () => video.removeEventListener('loadeddata', onLoaded);
            }
        }
    }, []);


    useEffect(() => {
        const updatePolylineClip = () => {
            if (!containerRef.current || !mediaRef.current || !polylineRef.current) { return; }
            const height = containerRef.current.offsetHeight + mediaRef.current.offsetHeight / 2;
            polylineRef.current.style.maxHeight = `${height}px`;
        };
        updatePolylineClip();
        window.addEventListener('resize', updatePolylineClip);
        return () => window.removeEventListener('resize', updatePolylineClip);
    }, []);

    // render
    return (
        <>
            <Head>
                {heroMedia?.src && heroMedia.contentType !== 'video/mp4' && (
                    <link
                        as='image'
                        fetchPriority='high'
                        href={`${heroMedia.src}?w=1440&fm=webp&q=50`}
                        rel='preload' />
                )}
                {heroMedia?.src && heroMedia.contentType === 'video/mp4' && (
                    <link
                        as='video'
                        href={heroMedia.src}
                        rel='preload'
                        type='video/mp4' />
                )}
            </Head>
            <Stickers shouldStart={shouldStartStickerIntro} />
            <div className='relative overflow-hidden'>
                <section
                    className={`animated-statement relative md:pt-[160px] pt-[112px] pb-40 md:pb-48 theme-${theme} theme- bg-theme-themed-surface-bg`}
                    ref={containerRef}>
                    <div className='relative z-10 layout-container layout-site-outer-padding w-full'>
                        {/* eyebrow */}
                        {eyebrow && (
                            <p
                                className='col-padding animation__mask-slide-in type-style-eyebrow text-text-accent -top-40 left-1/2 -translate-x-1/2 absolute ml-auto'
                                ref={eyebrowRef}>
                                {eyebrow}
                            </p>
                        )}
                        <div className={`col-wrapper flex flex-col ${isCenter ? ' items-center text-center' : ''}`}>

                            {/* hero text */}
                            {heroText && (
                                <h1 className='col-padding w-full sm:w-11/12 md:w-full md:max-w-[1032px] type-style-display-animated break-words text-theme-themed-text-accent animated-statement__text opacity-0'>
                                    {renderHeroText(heroText)}
                                </h1>
                            )}

                            {/* body copy */}
                            {bodyCopy && (
                                <p
                                    className='col-padding animation__mask-slide-in text-text-secondary type-style-body mt-24 sm:w-10/12 md:w-8/12'
                                    ref={bodyCopyRef}>
                                    {bodyCopy}
                                </p>
                            )}

                            {/* cta buttons */}
                            {(cta1Link || cta2Link) && (
                                <div
                                    className={`col-padding flex flex-wrap gap-8 mt-24${isCenter ? ' justify-center' : ''}`}
                                    ref={buttonsRef}>
                                    {cta1Link && (
                                        <Button
                                            as='a'
                                            className='button button--primary'
                                            href={formatURI(cta1Link)}>
                                            <span className='button__label--animate'>
                                                <span>{cta1Label}</span>
                                            </span>
                                            <span className='button__icon button__icon--animate'>
                                                <IconChevronSprite />
                                            </span>
                                        </Button>
                                    )}
                                    {cta2Link && (
                                        <Button
                                            as='a'
                                            className='button button--secondary'
                                            href={formatURI(cta2Link)}>
                                            <span className='button__label--animate'>
                                                <span>{cta2Label}</span>
                                            </span>
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>

                    </div>
                    {/* polyline */}
                    {PolylineIcon && (
                        <div
                            aria-hidden='true'
                            className='absolute top-0 left-1/2 w-[950px] z-[1] overflow-hidden'
                            ref={polylineRef}>
                            <PolylineIcon className='w-full h-auto text-theme-themed-polyline' />
                        </div>
                    )}
                </section>
                <div className='relative overflow-hidden [overflow-anchor:none]'>
                    <div className='bg-theme-themed-surface-bg w-full absolute h-1/2 top-0 left-0 z-0' />
                    <div
                        className='layout-container layout-site-outer-padding relative z-10'
                        ref={mediaRef}>
                        {heroMedia?.contentType === 'video/mp4' ? (
                            <BackgroundVideo
                                className='rounded object-cover'
                                containerClassName='w-full aspect-video rounded bg-theme-themed-surface-bg'
                                preload='auto'
                                src={heroMedia.src}/>
                        ) : (
                            <picture className='w-full aspect-video rounded bg-theme-themed-surface-bg block'>
                                <source
                                    srcSet={`${heroMedia?.src}?w=1440&fm=webp&q=50`}
                                    type='image/webp' />
                                <img
                                    alt=''
                                    className='rounded object-cover w-full h-full'
                                    fetchPriority='high'
                                    loading='eager'
                                    src={`${heroMedia?.src}?w=1440&q=50`} />
                            </picture>
                        )}
                    </div>
                </div>
                {statistics && (
                    <div ref={statRowRef}>
                        <ModuleStats
                            isHomePage={true}
                            itemsCollection={statistics}/>
                    </div>
                )}
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    alignment: PropTypes.string,
    bodyCopy: PropTypes.string,
    cta1Link: PropTypes.string,
    cta1Label: PropTypes.string,
    cta2Link: PropTypes.string,
    cta2Label: PropTypes.string,
    eyebrow: PropTypes.string,
    heroText: PropTypes.object,
    heroMedia: PropTypes.object,
    polyline: PropTypes.string,
    statistics: PropTypes.object,
    theme: PropTypes.string
};

HomePageHero.propTypes = propTypes;

export default HomePageHero;
